import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import "./TrainingLog.css";
import {
  deleteTrainingLog,
  getAllTrainingLogs,
} from "../../redux/trainingLogSlice";

const columnNames = [
  "Document Number",
  "Date of Training",
  "Trainer",
  "Trainer Signature",
  "Trainee",
  "Trainee Signature",
  "Revision Trained",
  "Training Status of Trainee",
  "Action",
];

const TrainingLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [visibleColumns, setVisibleColumns] = useState(columnNames);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(5);
  const [offset, setOffset] = useState(0);

  const { trainingLogs, loading, error, pagination } = useSelector(
    (state) => state.trainingLog
  );

  useEffect(() => {
    dispatch(
      getAllTrainingLogs({
        limit,
        offset,
        search: searchQuery || undefined,
      })
    );
  }, [dispatch, limit, offset, searchQuery]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this training log?")) {
      await dispatch(deleteTrainingLog(id));
      dispatch(
        getAllTrainingLogs({
          limit,
          offset,
          search: searchQuery || undefined,
        })
      );
    }
  };

  const handleAddRow = () => {
    navigate("/add-training-log");
  };

  return (
    <div>
      <AppBar title="Training Logs" buttons={[]} />
      <div className="search-header">
        <input
          type="text"
          placeholder="Search Training Logs..."
          className="search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-button" onClick={() => setSearchQuery("")}>
            x
          </button>
        )}
      </div>
      <div className="button-container">
        <button className="appbar-button" onClick={handleAddRow}>
          + Add Training Log
        </button>
      </div>
      {loading && <p>Loading Training Logs...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {!loading && !error && (
        <table className="table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trainingLogs.map((row) => (
              <tr key={row.training_log_id ?? row.uuid ?? row.id}>
                <td>{row.procedure_revision?.reference || "N/A"}</td>
                <td>{new Date(row.date_of_training).toLocaleDateString()}</td>
                <td>
                  {row.trainer
                    ? `${row.trainer.first_name} ${row.trainer.last_name}`
                    : "N/A"}
                </td>
                <td>
                  {row.trainer_signature?.data ? (
                    <img
                      src={`data:image/png;base64,${btoa(
                        String.fromCharCode(
                          ...new Uint8Array(row.trainer_signature.data)
                        )
                      )}`}
                      alt="Trainer Signature"
                      className="signature"
                    />
                  ) : (
                    "No Signature"
                  )}
                </td>
                <td>
                  {row.trainee
                    ? `${row.trainee.first_name} ${row.trainee.last_name}`
                    : "N/A"}
                </td>
                <td>
                  {row.trainee_signature?.data ? (
                    <img
                      src={`data:image/png;base64,${btoa(
                        String.fromCharCode(
                          ...new Uint8Array(row.trainee_signature.data)
                        )
                      )}`}
                      alt="Trainee Signature"
                      className="signature"
                    />
                  ) : (
                    "No Signature"
                  )}
                </td>
                <td>{row.version_id_ref || "N/A"}</td>
                <td>
                  {row.is_maintained_outside_system
                    ? "Maintained Outside System"
                    : "In System"}
                </td>
                <td className="action-buttons">
                  <button
                    className="edit-row"
                    onClick={() =>
                      navigate("/add-training-log", {
                        state: { editMode: true, log: row },
                      })
                    }
                  >
                    ✎
                  </button>
                  <button
                    className="delete-row"
                    onClick={() =>
                      handleDelete(
                        row.training_log_id ?? row.uuid ?? row.id
                      )
                    }
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="pagination">
        <button
          onClick={() => setOffset((prev) => Math.max(prev - limit, 0))}
          disabled={offset === 0}
        >
          Prev
        </button>
        <span>
          Page {offset / limit + 1} of {pagination?.totalPages || 1}
        </span>
        <button
          onClick={() => setOffset((prev) => prev + limit)}
          disabled={offset / limit + 1 >= (pagination?.totalPages || 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default TrainingLogs;
